import React, { useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import config from '../../config';

import LeadToolbar from './LeadToolbar';
import LeadsTable from './LeadsTable';
import LeadStatusModal from './LeadStatusModal';
import LinkBookingModal from './LinkBookingModal';
import { normalizePricingSnapshot } from './PricingSnapshotFields';
import CustomerDetailsModal from '../customers/CustomerDetailsModal';
import { useLeadsApi } from './useLeadsApi';
import {
  buildBookingLeadBody,
  BOOKING_LEAD_SOURCE,
  resolveLeadPhone,
  validateBookingLeadInputs,
} from '../../utils/bookingLeadsApi';
import s from './Leads.module.scss';

const POLLING_INTERVAL = 60000;
const REFRESH_INTERVAL_SECONDS = POLLING_INTERVAL / 1000;
const PAGE_SIZE = 20;
const ALL_LEAD_STATUSES = ['new', 'follow_up', 'converted', 'dropped'];

const EQUIPMENT_CLASS_MAP = {
  51: 'JCB - Backhoe Loader',
  52: 'Chota JCB',
  53: 'Mini Excavator',
  54: 'Excavator',
  55: 'Crane - 12 Ton',
  56: 'Crane - 14 Ton',
  57: 'Crane - 15 Ton',
};

const VEHICLE_CLASS_MAP = {
  1: 'Piaggio Ape',
  2: 'Tata Ace',
  3: 'Ashok Leyland Dost',
  4: 'Force Tempo',
  5: 'Tata 407',
  6: 'Eicher Pro 1049',
  7: 'Eicher Pro 1059',
  8: 'Eicher Pro 2080',
  9: 'Partner',
  10: 'Tata LPT 1109',
  11: 'Furio 18',
  12: 'Eicher Pro 6032',
  13: 'Tata LPT 1818',
  14: 'Ashok Leyland 2525',
  15: 'Tata Signa 3523',
};

const PRICING_FIELDS = [
  'customer_expectation_rate',
  'offered_rate',
  'best_transporter_rate',
  'best_transporter_id',
  'best_transporter_name',
];

const pricingDiffersFromLead = (lead, pricing) => {
  if (!pricing) return false;
  const current = normalizePricingSnapshot(
    Object.fromEntries(PRICING_FIELDS.map((field) => [field, lead[field]]))
  );
  return PRICING_FIELDS.some((field) => pricing[field] !== current[field]);
};

const LeadsPage = () => {
  const { list, get, getCounts, update, setStatus, scheduleFollowUp, addNote, listNotes, listActivities, listConversation, snooze, triggerOpsCall, create } = useLeadsApi();
  const history = useHistory();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('new');
  const [leads, setLeads] = useState([]);
  const [pinnedLead, setPinnedLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState(1);
  const [nextRefreshIn, setNextRefreshIn] = useState(REFRESH_INTERVAL_SECONDS);

  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [leadTypeFilter, setLeadTypeFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [regionLocation, setRegionLocation] = useState('');
  const [locationCounts, setLocationCounts] = useState([]);
  const [longTripStatuses, setLongTripStatuses] = useState(ALL_LEAD_STATUSES);
  const [leadSortBy, setLeadSortBy] = useState('recent');
  const [contactByFrom, setContactByFrom] = useState('');
  const [contactByTo, setContactByTo] = useState('');

  const [expandedLeadId, setExpandedLeadId] = useState(null);
  const [leadNotes, setLeadNotes] = useState({});
  const [leadNoteDraft, setLeadNoteDraft] = useState({});
  const [leadNoteSaving, setLeadNoteSaving] = useState({});
  const [leadActivities, setLeadActivities] = useState({});
  const [leadConversations, setLeadConversations] = useState({});
  const [leadConversationLoading, setLeadConversationLoading] = useState({});
  const [leadConversationError, setLeadConversationError] = useState({});

  const [editingLeadId, setEditingLeadId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLeadSaving, setEditLeadSaving] = useState(false);

  const [modal, setModal] = useState(null); // { variant, lead }
  const [linkLead, setLinkLead] = useState(null);
  const [customerDetailsModalOpen, setCustomerDetailsModalOpen] = useState(false);
  const [customerDetailsModalId, setCustomerDetailsModalId] = useState(null);
  const [createLeadOpen, setCreateLeadOpen] = useState(false);
  const [createLeadForm, setCreateLeadForm] = useState({
    phone_number: '', lead_type: 'future_booking', contact_by: new Date().toISOString().slice(0, 16),
    pickup_time: '', pickup_address_id: '', vehicle_class_id: '', remarks: '',
  });
  const [createLeadSaving, setCreateLeadSaving] = useState(false);
  const [leadUserSearch, setLeadUserSearch] = useState('');
  const [leadSelectedUser, setLeadSelectedUser] = useState(null);
  const [leadUserSuggestions, setLeadUserSuggestions] = useState([]);
  const [leadRecommendedAddresses, setLeadRecommendedAddresses] = useState([]);
  const [leadPickupAddress, setLeadPickupAddress] = useState(null);
  const [leadAddressQuery, setLeadAddressQuery] = useState('');
  const [leadAddressSuggestions, setLeadAddressSuggestions] = useState([]);
  const [leadAddressSearching, setLeadAddressSearching] = useState(null);
  const [leadDropAddresses, setLeadDropAddresses] = useState([]);
  const [leadDropAddressQuery, setLeadDropAddressQuery] = useState('');
  const [leadDropAddressSuggestions, setLeadDropAddressSuggestions] = useState([]);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      const trimmedSearch = searchTerm.trim();
      // Lead ids are short numbers. A phone search is 10+ digits and stays
      // inside the open tab. A lead-id lookup ignores the tab so New, Due,
      // Follow up, Converted, and Dropped are all included.
      const leadIdLookup = /^\d{1,9}$/.test(trimmedSearch);
      if (!leadIdLookup) {
        if (activeTab === 'due') {
          params.view = 'due';
          params.sortBy = 'contact_by';
          params.sortOrder = 'asc';
        }
        else if (activeTab === 'new') {
          params.status = 'new';
        } else if (activeTab === 'follow_up') {
          params.status = 'follow_up';
          params.sortBy = 'follow_up_at';
          params.sortOrder = 'asc';
        } else if (activeTab === 'dropped') {
          params.status = 'dropped';
          params.sortBy = 'dropped_at';
          params.sortOrder = 'desc';
        } else if (activeTab === 'all') {
          params.sortBy = 'id';
          params.sortOrder = 'desc';
        } else if (activeTab === 'long_trip') {
          params.region = 'long_trip';
          if (regionLocation) params.regionLocation = regionLocation;
          if (longTripStatuses.length && longTripStatuses.length < ALL_LEAD_STATUSES.length) {
            params.status = longTripStatuses.join(',');
          }
        } else if (activeTab !== 'all') params.status = activeTab;
      }
      if (trimmedSearch) params.search = trimmedSearch;
      if (sourceFilter) params.source = sourceFilter;
      if (leadTypeFilter) params.leadType = leadTypeFilter;
      if (regionFilter && activeTab !== 'long_trip') params.region = regionFilter;
      if (contactByFrom) params.contactByFrom = contactByFrom;
      if (contactByTo) params.contactByTo = contactByTo;
      if (leadSortBy && activeTab !== 'follow_up' && activeTab !== 'dropped' && activeTab !== 'due' && activeTab !== 'all') params.sortBy = leadSortBy;
      params.page = page;
      const result = await list(params);
      setLeads(result.leads);
      setTotal(result.total);
      setLocationCounts(result.locationCounts || []);
      if (activeTab === 'long_trip' && result.regionTotal != null) {
        setCounts((prev) => ({ ...prev, long_trip: result.regionTotal }));
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchTerm, sourceFilter, leadTypeFilter, regionFilter, regionLocation, longTripStatuses, contactByFrom, contactByTo, leadSortBy, page, list]);

  const fetchCounts = useCallback(async () => {
    const result = await getCounts();
    const statusCounts = result.counts || {};
    const all =
      (statusCounts.new || 0) +
      (statusCounts.follow_up || 0) +
      (statusCounts.converted || 0) +
      (statusCounts.dropped || 0) +
      (result.due || 0);
    setCounts({ ...statusCounts, due: result.due, all });
  }, [getCounts]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    const interval = setInterval(fetchLeads, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchLeads]);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  useEffect(() => {
    if (leads.length > 0) setNextRefreshIn(REFRESH_INTERVAL_SECONDS);
  }, [leads]);

  useEffect(() => {
    const timer = setInterval(() => {
      setNextRefreshIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setPageInput(page);
  }, [page]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setExpandedLeadId(null);
    setPinnedLead(null);
    setRegionLocation('');
    if (tab === 'long_trip') setRegionFilter('');
  };

  const handlePageChange = (p) => setPage(p);
  const handlePageInputChange = (e) => setPageInput(e.target.value);
  const handlePageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const num = parseInt(pageInput, 10);
      if (Number.isFinite(num) && num >= 1 && num <= Math.ceil(total / PAGE_SIZE)) {
        handlePageChange(num);
      } else {
        setPageInput(page);
      }
    }
  };
  const handlePageInputBlur = () => setPageInput(page);

  const handleClearFilters = () => {
    setLeadTypeFilter('');
    setRegionFilter('');
    setRegionLocation('');
    setLongTripStatuses(ALL_LEAD_STATUSES);
    setContactByFrom('');
    setContactByTo('');
    setPage(1);
  };

  const fetchLeadNotes = useCallback(async (leadId) => {
    const notes = await listNotes(leadId);
    setLeadNotes((prev) => ({ ...prev, [leadId]: notes }));
  }, [listNotes]);

  const fetchLeadActivities = useCallback(async (leadId) => {
    const activities = await listActivities(leadId);
    setLeadActivities((prev) => ({ ...prev, [leadId]: activities }));
  }, [listActivities]);

  const fetchLeadConversation = useCallback(async (lead) => {
    if (!lead?.conversation_id) {
      setLeadConversations((prev) => ({ ...prev, [lead.id]: null }));
      return;
    }
    setLeadConversationLoading((prev) => ({ ...prev, [lead.id]: true }));
    setLeadConversationError((prev) => ({ ...prev, [lead.id]: '' }));
    try {
      const conversation = await listConversation(lead);
      setLeadConversations((prev) => ({ ...prev, [lead.id]: conversation }));
    } catch (err) {
      setLeadConversationError((prev) => ({ ...prev, [lead.id]: err.message }));
    } finally {
      setLeadConversationLoading((prev) => ({ ...prev, [lead.id]: false }));
    }
  }, [listConversation]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawId = params.get('expandLead');
    const leadId = rawId ? parseInt(rawId, 10) : null;

    if (!leadId) {
      setPinnedLead(null);
      return;
    }

    setActiveTab('all');
    setPage(1);
    setExpandedLeadId(leadId);

    get(leadId)
      .then((lead) => {
        if (!lead || !lead.id) throw new Error('Lead not found');
        setPinnedLead(lead);
        fetchLeadNotes(leadId);
        fetchLeadActivities(leadId);
        fetchLeadConversation(lead);
      })
      .catch((err) => {
        console.error('Error loading lead from URL:', err);
        setPinnedLead(null);
        setExpandedLeadId(null);
        toast.error('Could not load lead details');
      });
  }, [location.search, get, fetchLeadNotes, fetchLeadActivities, fetchLeadConversation]);

  const displayLeads = pinnedLead && !leads.some((l) => l.id === pinnedLead.id)
    ? [pinnedLead, ...leads]
    : leads;

  const handleToggleExpand = (lead) => {
    if (expandedLeadId === lead.id) {
      setExpandedLeadId(null);
      if (pinnedLead && pinnedLead.id === lead.id) {
        setPinnedLead(null);
        const params = new URLSearchParams(location.search);
        params.delete('expandLead');
        history.replace(`${location.pathname}?${params.toString()}`);
      }
      return;
    }
    setExpandedLeadId(lead.id);
    fetchLeadNotes(lead.id);
    fetchLeadActivities(lead.id);
    fetchLeadConversation(lead);
  };

  const handleNoteDraftChange = (leadId, val) => {
    setLeadNoteDraft((prev) => ({ ...prev, [leadId]: val }));
  };

  const handleAddNote = async (leadId) => {
    const text = (leadNoteDraft[leadId] || '').trim();
    if (!text) return;
    try {
      setLeadNoteSaving((prev) => ({ ...prev, [leadId]: true }));
      await addNote(leadId, text);
      setLeadNoteDraft((prev) => ({ ...prev, [leadId]: '' }));
      fetchLeadNotes(leadId);
      toast.success('Note added');
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setLeadNoteSaving((prev) => ({ ...prev, [leadId]: false }));
    }
  };

  const handleSnooze = async (leadId, days) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead) return;
    try {
      await snooze(lead, days);
      fetchLeads();
      toast.success(`Snoozed ${days} day${days > 1 ? 's' : ''}`);
    } catch (err) {
      toast.error('Failed to snooze lead');
    }
  };

  const handleStartEdit = (lead) => {
    setEditingLeadId(lead.id);
    setEditForm({
      lead_type: lead.lead_type,
      contact_by: lead.contact_by ? lead.contact_by.slice(0, 16) : '',
      pickup_time: lead.pickup_time ? lead.pickup_time.slice(0, 16) : '',
      pickup_address_id: lead.pickup_address_id || '',
      drop_address_ids: Array.isArray(lead.drop_address_ids) ? lead.drop_address_ids : [],
      vehicle_class_id: lead.vehicle_class_id || '',
      remarks: lead.remarks || '',
      customer_expectation_rate: lead.customer_expectation_rate ?? '',
      offered_rate: lead.offered_rate ?? '',
      best_transporter_rate: lead.best_transporter_rate ?? '',
      best_transporter_id: lead.best_transporter_id ?? null,
      best_transporter_name: lead.best_transporter_name ?? '',
    });
  };

  const handleEditFormChange = (changes) => {
    setEditForm((f) => ({ ...f, ...changes }));
  };

  const handleSaveEdit = async (leadId) => {
    try {
      setEditLeadSaving(true);
      const body = {};
      if (editForm.lead_type !== undefined) body.lead_type = editForm.lead_type;
      if (editForm.contact_by !== undefined) body.contact_by = editForm.contact_by ? new Date(editForm.contact_by).toISOString() : null;
      if (editForm.pickup_time !== undefined) body.pickup_time = editForm.pickup_time ? new Date(editForm.pickup_time).toISOString() : null;
      if (editForm.pickup_address_id !== undefined && editForm.pickup_address_id !== '') body.pickup_address_id = parseInt(editForm.pickup_address_id);
      if (editForm.drop_address_ids !== undefined) {
        body.drop_address_ids = editForm.drop_address_ids.length > 0 ? editForm.drop_address_ids.map((id) => parseInt(id)) : [];
      }
      if (editForm.vehicle_class_id !== undefined && editForm.vehicle_class_id !== '') body.vehicle_class_id = parseInt(editForm.vehicle_class_id);
      if (editForm.remarks !== undefined) body.remarks = editForm.remarks;
      Object.assign(body, normalizePricingSnapshot(editForm));
      await update(leadId, body);
      setEditingLeadId(null);
      fetchLeads();
      toast.success('Lead updated');
    } catch (err) {
      toast.error('Failed to update lead');
    } finally {
      setEditLeadSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingLeadId(null);
  };

  const handleStatusChange = (action, lead) => {
    if (action === 'follow_up') {
      setModal({ variant: 'follow_up', lead });
    } else if (action === 'reschedule') {
      setModal({ variant: 'follow_up', lead });
    } else if (action === 'drop') {
      setModal({ variant: 'drop', lead });
    } else if (action === 'reset') {
      setModal({ variant: 'reset', lead });
    } else if (action === 'convert') {
      handleConvertLead(lead);
    } else if (action === 'link') {
      setLinkLead(lead);
    }
  };

  const handleLinkLead = async (lead, booking, pricing) => {
    if (!lead?.id || !booking?.id) return;
    await setStatus(lead.id, 'converted', booking.id, null, pricing);
    toast.success(`Lead #${lead.id} linked to Booking #${booking.id}`);
    fetchLeads();
    fetchCounts();
    fetchLeadNotes(lead.id);
    setLinkLead(null);
    setExpandedLeadId(null);
  };

  const handleModalConfirm = async (primary, secondary, tertiary) => {
    const { variant, lead } = modal;
    try {
      if (variant === 'follow_up') {
        if (!primary) return;
        await scheduleFollowUp(lead.id, primary, secondary);
        if (pricingDiffersFromLead(lead, tertiary)) await update(lead.id, tertiary);
        toast.success(`Follow-up scheduled for ${new Date(primary).toLocaleDateString()}`);
      } else if (variant === 'pricing') {
        await update(lead.id, primary);
        toast.success('Pricing snapshot saved');
      } else if (variant === 'drop') {
        await setStatus(lead.id, 'dropped', null, primary, secondary);
        toast.success(`Lead #${lead.id} dropped`);
      } else if (variant === 'pricing_convert') {
        await update(lead.id, primary);
        navigateToBooking(lead);
        toast.success('Pricing snapshot saved');
      } else if (variant === 'reset') {
        await setStatus(lead.id, 'new');
        toast.success(`Lead #${lead.id} reset to new`);
      }
      fetchLeads();
      fetchCounts();
      if (variant === 'follow_up' && secondary) fetchLeadNotes(lead.id);
    } catch (err) {
      toast.error(err.message);
    }
    setModal(null);
    // Pricing is edited from inside an expanded lead, so keep that row open to show the result.
    if (variant !== 'pricing') setExpandedLeadId(null);
  };

  const handlePhoneClick = (lead) => {
    setModal({ variant: 'voice_call', lead });
  };

  const handleCustomerClick = (lead) => {
    const customerId = lead?.customer_id ?? lead?.customer?.id ?? null;
    if (!customerId) {
      toast.error('Customer details not available for this lead');
      return;
    }
    setCustomerDetailsModalId(customerId);
    setCustomerDetailsModalOpen(true);
  };

  const handleVoiceCall = async (selectedPhone) => {
    const { lead } = modal;
    if (!lead) return;
    let phone = String(selectedPhone || lead.customer_phone || lead.phone_number || '');
    const digits = phone.replace(/\D/g, '');
    const normalizedPhone = digits.length === 10 ? `91${digits}` : digits;
    try {
      await triggerOpsCall({
        customerPhone: normalizedPhone,
        customerId: lead.customer_id || null,
      });
      toast.success(`Call initiated to ${lead.customer_name || lead.phone_number}`);
    } catch (err) {
      toast.error(err.message || 'Failed to place call');
    }
    setModal(null);
  };

  const navigateToBooking = (lead) => {
    const params = new URLSearchParams();
    if (lead.customer_id) params.append('customerId', lead.customer_id);
    if (lead.customer_name) params.append('customerName', lead.customer_name);
    if (lead.customer_phone || lead.phone_number) params.append('customerPhone', lead.customer_phone || lead.phone_number);
    if (lead.pickup_time) params.append('pickupTime', lead.pickup_time);
    if (lead.pickup_address_id) params.append('pickupAddressId', lead.pickup_address_id);
    if (lead.drop_address_ids && lead.drop_address_ids.length > 0) {
      params.append('dropAddressIds', lead.drop_address_ids.join(','));
    }
    if (lead.vehicle_class_id) params.append('vehicleClassId', lead.vehicle_class_id);
    if (lead.remarks) params.append('remarks', lead.remarks);
    if (lead.lead_type === 'part_load_interest') params.append('bookingType', 'part');
    params.append('sourceLeadId', lead.id);
    history.push(`/app/customers/new-booking?${params.toString()}`);
  };

  const handleConvertLead = (lead) => {
    const hasPricingSnapshot = [
      lead.customer_expectation_rate,
      lead.offered_rate,
      lead.best_transporter_rate,
    ].every((value) => value != null && value !== '');
    if (hasPricingSnapshot) {
      navigateToBooking(lead);
      return;
    }
    setModal({ variant: 'pricing_convert', lead });
  };

  const handleRefresh = () => {
    setNextRefreshIn(REFRESH_INTERVAL_SECONDS);
    fetchLeads();
    fetchCounts();
  };

  // ── Create lead helpers ──

  const handleLeadUserSelect = useCallback(async (user) => {
    let customerId = null;
    try {
      const custRes = await fetch(`${config.customerApiBase}/users/${user.id}/customer`);
      if (custRes.ok) {
        const custData = await custRes.json();
        customerId = custData.customer_id ?? null;
      }
    } catch { /* ignore */ }

    const selected = { ...user, customer_id: customerId };
    setLeadSelectedUser(selected);
    setLeadUserSearch(`${user.name || 'Unknown'} (${user.phone})`);
    setLeadUserSuggestions([]);
    setCreateLeadForm((f) => ({ ...f, phone_number: user.phone }));

    if (!customerId) return;
    try {
      const res = await fetch(`${config.customerApiBase}/customers/${customerId}/address-recommendations`);
      if (res.ok) {
        const data = await res.json();
        setLeadRecommendedAddresses(data.addresses || []);
      }
    } catch { /* ignore */ }
  }, []);

  const handleLeadUserSearch = useCallback(async (query) => {
    setLeadUserSearch(query);
    if (query.trim().length < 2) { setLeadUserSuggestions([]); return; }
    try {
      const res = await fetch(`${config.customerApiBase}/users/search?query=${encodeURIComponent(query)}`);
      if (!res.ok) return;
      const data = await res.json();
      const users = data.users || [];
      setLeadUserSuggestions(users);
      // Auto-select when ops paste/type a full phone without clicking the dropdown
      const qDigits = query.replace(/\D/g, '');
      if (users.length === 1 && qDigits.length >= 10) {
        const pDigits = String(users[0].phone || '').replace(/\D/g, '');
        if (pDigits === qDigits || pDigits.endsWith(qDigits) || qDigits.endsWith(pDigits)) {
          await handleLeadUserSelect(users[0]);
        }
      }
    } catch { /* ignore */ }
  }, [handleLeadUserSelect]);

  const clearLeadSelectedUser = () => {
    setLeadSelectedUser(null);
    setLeadUserSearch('');
    setLeadUserSuggestions([]);
    setLeadRecommendedAddresses([]);
    setLeadPickupAddress(null);
    setLeadDropAddresses([]);
    setCreateLeadForm((f) => ({ ...f, phone_number: '' }));
  };

  const getLeadAddressDisplay = (addr) => {
    if (!addr) return '';
    const parts = [addr.locality, addr.city, addr.state].filter(Boolean);
    return parts.join(', ') || addr.text || `Address #${addr.id}`;
  };

  const handleLeadAddressSearch = async (type) => {
    const query = type === 'pickup' ? leadAddressQuery : leadDropAddressQuery;
    if (query.trim().length < 3) return;
    setLeadAddressSearching(type);
    try {
      const res = await fetch(`${config.customerApiBase}/resolve-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address_text: query }),
      });
      if (!res.ok) { setLeadAddressSearching(null); return; }
      const data = await res.json();
      const suggestions = data.results || [];
      if (type === 'pickup') setLeadAddressSuggestions(suggestions);
      else setLeadDropAddressSuggestions(suggestions);
    } catch { /* ignore */ }
    setLeadAddressSearching(null);
  };

  const applyLeadAddressSuggestion = (suggestion, type) => {
    if (type === 'pickup') {
      setLeadPickupAddress({ id: suggestion.id, text: getLeadAddressDisplay(suggestion) });
      setLeadAddressSuggestions([]);
      setLeadAddressQuery('');
    } else {
      setLeadDropAddresses((prev) => [...prev, { id: suggestion.id, text: getLeadAddressDisplay(suggestion) }]);
      setLeadDropAddressSuggestions([]);
      setLeadDropAddressQuery('');
    }
  };

  const removeLeadDropAddress = (idx) => {
    setLeadDropAddresses((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCreateLead = async () => {
    const resolvedPhone = resolveLeadPhone({
      formPhone: createLeadForm.phone_number,
      selectedUserPhone: leadSelectedUser?.phone,
      searchQuery: leadUserSearch,
    });
    const validationError = validateBookingLeadInputs({
      phone: resolvedPhone,
      leadType: createLeadForm.lead_type,
      contactBy: createLeadForm.contact_by,
      hasSearchText: !!leadUserSearch.trim(),
      hasSelectedCustomer: !!leadSelectedUser,
      requireExistingCustomer: true,
      customerId: leadSelectedUser?.customer_id,
    });
    if (validationError) {
      toast.error(validationError);
      return;
    }
    try {
      setCreateLeadSaving(true);
      const body = buildBookingLeadBody({
        phone: resolvedPhone,
        leadType: createLeadForm.lead_type,
        contactBy: createLeadForm.contact_by,
        source: BOOKING_LEAD_SOURCE.MANUAL,
        customerId: leadSelectedUser?.customer_id,
        pickupTime: createLeadForm.pickup_time,
        pickupAddressId: leadPickupAddress?.id,
        dropAddressIds: leadDropAddresses.map((a) => a.id).filter(Boolean),
        vehicleClassId: createLeadForm.vehicle_class_id || undefined,
        remarks: createLeadForm.remarks,
      });
      const data = await create(body);
      toast.success(`Lead #${data.lead_id} created`);
      if (data.duplicate_warning) {
        toast.warn(
          `Duplicate active lead exists: #${data.duplicate_warning.existing_lead_id} (${data.duplicate_warning.status})`
        );
      }
      setCreateLeadOpen(false);
      setCreateLeadForm({
        phone_number: '', lead_type: 'future_booking', contact_by: new Date().toISOString().slice(0, 16),
        pickup_time: '', pickup_address_id: '', vehicle_class_id: '', remarks: '',
      });
      setLeadUserSearch('');
      setLeadSelectedUser(null);
      setLeadRecommendedAddresses([]);
      setLeadPickupAddress(null);
      setLeadDropAddresses([]);
      setLeadAddressQuery('');
      setLeadAddressSuggestions([]);
      setLeadDropAddressQuery('');
      setLeadDropAddressSuggestions([]);
      setLeadAddressSearching(null);
      fetchLeads();
      fetchCounts();
    } catch (err) {
      toast.error(err.message || 'Failed to create lead');
    } finally {
      setCreateLeadSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const now = new Date();
  const workdayProgress = Math.max(0, Math.min(1, (now.getHours() - 9) / 8));
  const dueUrgency = (counts.due || 0) > 0 ? workdayProgress : 0;

  return (
    <div className={s.root}>
      <div className={s.leadPageHeader}>
        <div className={s.leadPageHeaderLeft}>
          <h1 className={s.leadPageTitle}>
            <div className={s.titleIcon}><i className="fa fa-inbox" /></div>
            Leads
            <span className={s.countBadge}>
              {counts[activeTab] !== undefined ? counts[activeTab] : total}
            </span>
          </h1>
          <p className={s.subtitle}>View and manage customer leads</p>
        </div>
        <div className={s.leadPageHeaderRight}>
          <button className={s.btnNewBooking} onClick={() => setCreateLeadOpen(true)}>
            <i className="fa fa-plus" /> New Lead
          </button>
          <button className={s.refreshBtn} onClick={handleRefresh} disabled={loading} title={`Auto-refreshing in ${nextRefreshIn}s`}>
            <i className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-refresh'}`} />
            {!loading && (
              <div className={s.refreshProgressTrack}>
                <div className={s.refreshProgressBar} style={{ width: `${(nextRefreshIn / REFRESH_INTERVAL_SECONDS) * 100}%` }} />
              </div>
            )}
          </button>
        </div>
      </div>

      <LeadToolbar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        counts={counts}
        dueUrgency={dueUrgency}
        searchTerm={searchTerm}
        onSearchChange={(v) => { setSearchTerm(v); setPage(1); }}
        sourceFilter={sourceFilter}
        onSourceFilterChange={(v) => { setSourceFilter(v); setPage(1); }}
        leadTypeFilter={leadTypeFilter}
        onLeadTypeFilterChange={(v) => { setLeadTypeFilter(v); setPage(1); }}
        regionFilter={regionFilter}
        onRegionFilterChange={(v) => { setRegionFilter(v); setPage(1); }}
        regionLocation={regionLocation}
        onRegionLocationChange={(v) => { setRegionLocation(v); setPage(1); }}
        locationCounts={locationCounts}
        longTripStatuses={longTripStatuses}
        onLongTripStatusesChange={(next) => { setLongTripStatuses(next); setPage(1); }}
        leadSortBy={leadSortBy}
        onLeadSortByChange={(v) => { setLeadSortBy(v); setPage(1); }}
        contactByFrom={contactByFrom}
        onContactByFromChange={(v) => { setContactByFrom(v); setPage(1); }}
        contactByTo={contactByTo}
        onContactByToChange={(v) => { setContactByTo(v); setPage(1); }}
        onClearFilters={handleClearFilters}
        onCreateLead={() => setCreateLeadOpen(true)}
        onRefresh={handleRefresh}
        refreshing={loading}
      />

      <LeadsTable
        leads={displayLeads}
        leadsLoading={loading}
        error={error}
        activeTab={activeTab}
        expandedLeadId={expandedLeadId}
        expandedData={{}}
        editingLeadId={editingLeadId}
        editForm={editForm}
        editLeadSaving={editLeadSaving}
        leadNotes={leadNotes}
        leadNoteDraft={leadNoteDraft}
        leadNoteSaving={leadNoteSaving}
        leadActivities={leadActivities}
        leadConversations={leadConversations}
        leadConversationLoading={leadConversationLoading}
        leadConversationError={leadConversationError}
        searchTerm={searchTerm}
        onToggleExpand={handleToggleExpand}
        onStartEdit={handleStartEdit}
        onSaveEdit={handleSaveEdit}
        onCancelEdit={handleCancelEdit}
        onEditFormChange={handleEditFormChange}
        onNoteDraftChange={handleNoteDraftChange}
        onAddNote={handleAddNote}
        onSnooze={handleSnooze}
        onEditPricing={(lead) => setModal({ variant: 'pricing', lead })}
        onStatusChange={handleStatusChange}
        onPhoneClick={handlePhoneClick}
        onCustomerClick={handleCustomerClick}
        pagination={{
          page, pageInput, totalPages,
          onPageChange: handlePageChange,
          onPageInputChange: handlePageInputChange,
          onPageInputKeyDown: handlePageInputKeyDown,
          onPageInputBlur: handlePageInputBlur,
        }}
      />

      {modal && (
        modal.variant === 'voice_call' ? (
          <LeadStatusModal
            key={`${modal.variant}-${modal.lead.id}`}
            variant="voice_call"
            lead={modal.lead}
            onConfirm={handleVoiceCall}
            onClose={() => setModal(null)}
          />
        ) : (
          <LeadStatusModal
            key={`${modal.variant}-${modal.lead.id}`}
            variant={modal.variant}
            lead={modal.lead}
            onConfirm={handleModalConfirm}
            onClose={() => setModal(null)}
          />
        )
      )}

      {linkLead && (
        <LinkBookingModal
          lead={linkLead}
          onLink={handleLinkLead}
          onClose={() => setLinkLead(null)}
        />
      )}

      <CustomerDetailsModal
        isOpen={customerDetailsModalOpen}
        toggle={() => setCustomerDetailsModalOpen(false)}
        customerId={customerDetailsModalId}
        onViewAllCalls={(phone) => {
          setCustomerDetailsModalOpen(false);
          history.push(`/app/call-manager?view=call-logs&phone=${encodeURIComponent(phone || '')}`);
        }}
      />

      {createLeadOpen && (
        <div className={s.modalBackdrop} onClick={() => setCreateLeadOpen(false)}>
          <div className={s.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <div className={s.modalHeader}>
              <i className="fa fa-plus-circle" />
              <h3>New Booking Lead</h3>
            </div>
            <div className={s.modalBody}>
              {/* Customer Search */}
              <div className={s.formField}>
                <label className={s.formLabel}>Customer <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <input
                    className={s.formInput}
                    type="text"
                    placeholder="Search by name or phone number"
                    value={leadUserSearch}
                    onChange={(e) => handleLeadUserSearch(e.target.value)}
                    disabled={!!leadSelectedUser}
                  />
                  {leadSelectedUser && (
                    <button
                      style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
                      onClick={clearLeadSelectedUser}
                      type="button"
                    >
                      <i className="fa fa-times" />
                    </button>
                  )}
                </div>
                {leadUserSuggestions.length > 0 && !leadSelectedUser && (
                  <div className={s.suggestionsDropdown}>
                    {leadUserSuggestions.map((user) => (
                      <button
                        key={user.id}
                        className={s.suggestionItem}
                        onClick={() => handleLeadUserSelect(user)}
                        type="button"
                      >
                        <span style={{ fontWeight: 600 }}>{user.name || 'Unknown'}</span>
                        <span style={{ color: '#6b7280', marginLeft: '8px' }}>({user.phone})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className={s.formField}>
                <label className={s.formLabel}>Lead Type <span style={{ color: '#ef4444' }}>*</span></label>
                <select
                  className={s.formInput}
                  value={createLeadForm.lead_type}
                  onChange={(e) => setCreateLeadForm((f) => ({ ...f, lead_type: e.target.value }))}
                >
                  <option value="future_booking">Future Booking</option>
                  <option value="part_load_interest">Part Load Interest</option>
                  <option value="general_inquiry">General Inquiry</option>
                  <option value="equipment_interest">Equipment Interest</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className={s.formField}>
                <label className={s.formLabel}>Contact By <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  className={s.formInput}
                  type="datetime-local"
                  value={createLeadForm.contact_by}
                  onChange={(e) => setCreateLeadForm((f) => ({ ...f, contact_by: e.target.value }))}
                />
              </div>

              <div className={s.formField}>
                <label className={s.formLabel}>Pickup Time (optional)</label>
                <input
                  className={s.formInput}
                  type="datetime-local"
                  value={createLeadForm.pickup_time}
                  onChange={(e) => setCreateLeadForm((f) => ({ ...f, pickup_time: e.target.value }))}
                />
              </div>

              {/* Pickup Address */}
              <div className={s.formField}>
                <label className={s.formLabel}>Pickup Address (optional)</label>
                {leadSelectedUser && leadRecommendedAddresses.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '0.8rem', color: '#6b7280', width: '100%', marginBottom: '2px' }}>Saved addresses:</span>
                    {leadRecommendedAddresses.map((addr) => (
                      <button
                        key={addr.id}
                        type="button"
                        style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #d1d5db', background: leadPickupAddress?.id === addr.id ? '#dbeafe' : '#fff', color: '#374151', cursor: 'pointer', fontSize: '0.8rem' }}
                        onClick={() => setLeadPickupAddress({ id: addr.id, text: getLeadAddressDisplay(addr) })}
                      >
                        {getLeadAddressDisplay(addr)}
                      </button>
                    ))}
                  </div>
                )}
                {!leadPickupAddress && (
                  <div className={s.addressRow}>
                    <input
                      className={s.formInput}
                      type="text"
                      placeholder="Enter pickup address..."
                      value={leadAddressQuery}
                      onChange={(e) => {
                        setLeadAddressQuery(e.target.value);
                        setLeadAddressSuggestions([]);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); handleLeadAddressSearch('pickup'); }
                      }}
                    />
                    <button
                      type="button"
                      className={s.addressSearchBtn}
                      onClick={() => handleLeadAddressSearch('pickup')}
                      disabled={leadAddressQuery.trim().length < 3 || leadAddressSearching === 'pickup'}
                    >
                      {leadAddressSearching === 'pickup' ? <i className="fa fa-spinner fa-spin" /> : 'Search'}
                    </button>
                  </div>
                )}
                {leadAddressSuggestions.length > 0 && !leadPickupAddress && (
                  <div className={s.suggestionsBox}>
                    <div className={s.suggestionsHeader}>
                      <i className="fa fa-map-marker" />
                      Select an address:
                    </div>
                    {leadAddressSuggestions.map((suggestion, idx) => (
                      <button
                        key={`${suggestion.id || 's'}-${idx}`}
                        type="button"
                        className={s.suggestionItem}
                        onClick={() => applyLeadAddressSuggestion(suggestion, 'pickup')}
                      >
                        <strong>{suggestion.locality || suggestion.text}</strong>
                        {(suggestion.city || suggestion.state) && (
                          <small>
                            {[suggestion.city, suggestion.state, suggestion.pin_code].filter(Boolean).join(', ')}
                            {suggestion.similarity_score != null && (
                              <span className={s.matchScore}>{Math.round(suggestion.similarity_score * 100)}% match</span>
                            )}
                          </small>
                        )}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={s.suggestionsClose}
                      onClick={() => setLeadAddressSuggestions([])}
                    >
                      Close
                    </button>
                  </div>
                )}
                {leadPickupAddress && (
                  <div className={s.addressChip}>
                    <span>
                      <i className="fa fa-check-circle" />
                      {leadPickupAddress.text}
                    </span>
                    <button
                      type="button"
                      className={s.addressChipClear}
                      onClick={() => { setLeadPickupAddress(null); setLeadAddressQuery(''); setLeadAddressSuggestions([]); }}
                      aria-label="Remove pickup address"
                    >
                      <i className="fa fa-times" />
                    </button>
                  </div>
                )}
              </div>

              {/* Drop Addresses */}
              <div className={s.formField}>
                <label className={s.formLabel}>Drop Addresses (optional)</label>
                {leadDropAddresses.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                    {leadDropAddresses.map((addr, idx) => (
                      <div key={idx} className={s.dropAddressItem}>
                        <span style={{ flex: 1 }}>{addr.text}</span>
                        <button type="button" className={s.addressChipClear} onClick={() => removeLeadDropAddress(idx)}>
                          <i className="fa fa-times" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className={s.addressRow}>
                  <input
                    className={s.formInput}
                    type="text"
                    placeholder="Enter drop address..."
                    value={leadDropAddressQuery}
                    onChange={(e) => { setLeadDropAddressQuery(e.target.value); setLeadDropAddressSuggestions([]); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLeadAddressSearch('drop'); } }}
                  />
                  <button
                    type="button"
                    className={s.addressSearchBtn}
                    onClick={() => handleLeadAddressSearch('drop')}
                    disabled={leadDropAddressQuery.trim().length < 3 || leadAddressSearching === 'drop'}
                  >
                    {leadAddressSearching === 'drop' ? <i className="fa fa-spinner fa-spin" /> : 'Search'}
                  </button>
                </div>
                {leadDropAddressSuggestions.length > 0 && (
                  <div className={s.suggestionsBox}>
                    <div className={s.suggestionsHeader}>
                      <i className="fa fa-map-marker" />
                      Select an address:
                    </div>
                    {leadDropAddressSuggestions.map((suggestion, idx) => (
                      <button
                        key={`${suggestion.id || 's'}-${idx}`}
                        type="button"
                        className={s.suggestionItem}
                        onClick={() => applyLeadAddressSuggestion(suggestion, 'drop')}
                      >
                        <strong>{suggestion.locality || suggestion.text}</strong>
                        {(suggestion.city || suggestion.state) && (
                          <small>
                            {[suggestion.city, suggestion.state, suggestion.pin_code].filter(Boolean).join(', ')}
                            {suggestion.similarity_score != null && (
                              <span className={s.matchScore}>{Math.round(suggestion.similarity_score * 100)}% match</span>
                            )}
                          </small>
                        )}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={s.suggestionsClose}
                      onClick={() => setLeadDropAddressSuggestions([])}
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>

              <div className={s.formField}>
                <label className={s.formLabel}>Vehicle Class (optional)</label>
                <select
                  className={s.formInput}
                  value={createLeadForm.vehicle_class_id || ''}
                  onChange={(e) => setCreateLeadForm((f) => ({ ...f, vehicle_class_id: e.target.value ? parseInt(e.target.value) : '' }))}
                >
                  <option value="">-- Select Vehicle --</option>
                  <optgroup label="Trucks">
                    {Object.entries(VEHICLE_CLASS_MAP).map(([id, name]) => (
                      <option key={id} value={id}>Class {id} - {name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Equipment">
                    {Object.entries(EQUIPMENT_CLASS_MAP).map(([id, name]) => (
                      <option key={id} value={id}>Class {id} - {name}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div className={s.formField}>
                <label className={s.formLabel}>Remarks</label>
                <textarea
                  className={s.formInput}
                  rows={3}
                  placeholder="What did the customer say? Any context for the operator?"
                  value={createLeadForm.remarks}
                  onChange={(e) => setCreateLeadForm((f) => ({ ...f, remarks: e.target.value }))}
                />
              </div>
            </div>
            <div className={s.modalActions} style={{ padding: '0 24px 20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className={s.modalCancelBtn} onClick={() => setCreateLeadOpen(false)}>Cancel</button>
              <button className={s.modalConfirmBtn} onClick={handleCreateLead} disabled={createLeadSaving}>
                {createLeadSaving ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-check" />}
                {createLeadSaving ? ' Saving...' : ' Create Lead'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPage;
